import { ref } from 'vue'

// ─────────────────────────────────────────────────────────────────────────────
// useChatSessions — manages the list of stored chat sessions for the current
// (user, department, reportId, sessionDate) tuple. Persistence lives on the
// server (AIReport.ChatSessions); this composable just talks to the REST API.
// ─────────────────────────────────────────────────────────────────────────────

const sessions  = ref([])
const isLoading = ref(false)
const loadError = ref(null)

const STORAGE_KEY = 'uprint_auth_token'
function authToken() {
  try { return localStorage.getItem(STORAGE_KEY) || '' } catch { return '' }
}

export async function fetchSessionList({ department, reportId, sessionDate, limit = 50, asUserId = null } = {}) {
  const token = authToken()
  if (!token) {
    sessions.value = []
    return []
  }
  isLoading.value = true
  loadError.value = null
  try {
    const params = new URLSearchParams()
    params.set('token', token)
    if (department)  params.set('department',  department)
    if (reportId)    params.set('reportId',    reportId)
    if (sessionDate) params.set('sessionDate', sessionDate)
    if (asUserId)    params.set('asUserId',    String(asUserId))
    params.set('limit', String(limit))

    const resp = await fetch(`/api/chat/sessions?${params.toString()}`)
    const data = await resp.json()
    if (!resp.ok || !data.success) throw new Error(data?.message || `HTTP ${resp.status}`)
    sessions.value = data.sessions || []
    return sessions.value
  } catch (err) {
    loadError.value = err.message
    return []
  } finally {
    isLoading.value = false
  }
}

// Admin-only: list user yang punya minimal satu chat session. Hasil dipakai
// untuk carousel tab user di header riwayat (super-user view). Mengembalikan
// array kosong jika non-admin atau request gagal — caller cukup cek length.
export async function fetchChatUsersForAdmin() {
  const token = authToken()
  if (!token) return []
  try {
    const resp = await fetch(`/api/chat/admin/users?token=${encodeURIComponent(token)}`)
    const data = await resp.json()
    if (!resp.ok || !data.success) return []
    return Array.isArray(data.users) ? data.users : []
  } catch {
    return []
  }
}

export async function fetchSessionDetail(sessionId) {
  const token = authToken()
  if (!token || !sessionId) return null
  try {
    const resp = await fetch(`/api/chat/sessions/${sessionId}?token=${encodeURIComponent(token)}`)
    const data = await resp.json()
    if (!resp.ok || !data.success) throw new Error(data?.message || `HTTP ${resp.status}`)
    return data.session
  } catch (err) {
    loadError.value = err.message
    return null
  }
}

export async function deleteSession(sessionId) {
  const token = authToken()
  if (!token || !sessionId) return false
  try {
    const resp = await fetch(`/api/chat/sessions/${sessionId}?token=${encodeURIComponent(token)}`, {
      method: 'DELETE',
    })
    const data = await resp.json()
    if (!resp.ok || !data.success) throw new Error(data?.message || `HTTP ${resp.status}`)
    sessions.value = sessions.value.filter(s => s.sessionId !== sessionId)
    return true
  } catch (err) {
    loadError.value = err.message
    return false
  }
}

// Fork (cabang) sebuah sesi — server akan menyalin pesan-pesan sampai
// fromMessageId (inklusif). Tanpa fromMessageId = duplikat seluruh percakapan.
// Mengembalikan detail sesi baru (lengkap dengan messages) atau null.
export async function forkSession(sessionId, { fromMessageId = null, title = null } = {}) {
  const token = authToken()
  if (!token || !sessionId) return null
  try {
    const resp = await fetch(`/api/chat/sessions/${sessionId}/fork?token=${encodeURIComponent(token)}`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ fromMessageId, title }),
    })
    const data = await resp.json()
    if (!resp.ok || !data.success) throw new Error(data?.message || `HTTP ${resp.status}`)
    return data.session
  } catch (err) {
    loadError.value = err.message
    return null
  }
}

export async function renameSession(sessionId, title) {
  const token = authToken()
  if (!token || !sessionId || !title) return false
  try {
    const resp = await fetch(`/api/chat/sessions/${sessionId}?token=${encodeURIComponent(token)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ title }),
    })
    const data = await resp.json()
    if (!resp.ok || !data.success) throw new Error(data?.message || `HTTP ${resp.status}`)
    const idx = sessions.value.findIndex(s => s.sessionId === sessionId)
    if (idx !== -1) sessions.value[idx] = { ...sessions.value[idx], title }
    return true
  } catch (err) {
    loadError.value = err.message
    return false
  }
}

// ── Multi-tab open state ─────────────────────────────────────────────────────
// Tab terbuka disimpan di DB (kolom IsOpenTab/TabOrder pada ChatSessions) agar
// restore setelah reload. Helper di bawah ini cuma RPC tipis ke endpoint.

export async function fetchOpenTabs(sessionDate = null) {
  const token = authToken()
  if (!token) return []
  try {
    const params = new URLSearchParams({ token })
    if (sessionDate) params.set('sessionDate', sessionDate)
    const resp = await fetch(`/api/chat/tabs?${params.toString()}`)
    const data = await resp.json()
    if (!resp.ok || !data.success) return []
    return Array.isArray(data.tabs) ? data.tabs : []
  } catch {
    return []
  }
}

// Tandai sebuah sesi sebagai tab terbuka / tertutup. tabOrder opsional.
export async function setTabOpen(sessionId, isOpen, tabOrder = null) {
  const token = authToken()
  if (!token || !sessionId) return false
  try {
    const resp = await fetch(`/api/chat/tabs?token=${encodeURIComponent(token)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionId, isOpen, tabOrder }),
    })
    const data = await resp.json()
    return !!(resp.ok && data.success)
  } catch {
    return false
  }
}

// Simpan urutan tab kiri→kanan (array sessionId).
export async function persistTabOrder(orderedIds) {
  const token = authToken()
  if (!token || !Array.isArray(orderedIds) || !orderedIds.length) return false
  try {
    const resp = await fetch(`/api/chat/tabs/order?token=${encodeURIComponent(token)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ orderedIds }),
    })
    const data = await resp.json()
    return !!(resp.ok && data.success)
  } catch {
    return false
  }
}

export function useChatSessions() {
  return {
    sessions,
    isLoading,
    loadError,
    fetchSessionList,
    fetchSessionDetail,
    deleteSession,
    renameSession,
    forkSession,
  }
}
